"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Client = void 0;
const utils_1 = require("./utils");
const TextOperation_1 = require("./TextOperation");
const types_1 = require("./types");
const event_1 = require("../event");
class Client {
    incomingOperationEmitter = new event_1.Emitter();
    onIncomingOperation = this.incomingOperationEmitter.event;
    operationAckEmitter = new event_1.Emitter();
    onOperationAck = this.operationAckEmitter.event;
    outgoingOperationEmitter = new event_1.Emitter();
    onOutgoingOperation = this.outgoingOperationEmitter.event;
    documentChangeEmitter = new event_1.Emitter();
    onDocumentChange = this.documentChangeEmitter.event;
    // Current document content state
    _document;
    // Last known server document state, based on incoming operations and
    // acknowledged outgoing operations. This is used to sync server state after
    // disconnects
    _serverDocument;
    // Current revision of the document
    revision;
    // Callback to send an operation
    sendOperation;
    sendOperationAck;
    // Operation that has been sent to the server but hasn't been acknowledged yet
    pendingOperation = null;
    // Any edits that the user has done since the pendingOperation, get combined into a queued operation
    // and gets sent from the moment pending operation has been acknowledged
    queuedOperation = null;
    constructor(document, revision, sendOperation, sendOperationAck) {
        this._document = document;
        this._serverDocument = document;
        this.revision = revision;
        this.sendOperation = sendOperation;
        this.sendOperationAck = sendOperationAck;
    }
    /** Check if client has pending operation, used for testing */
    get hasPendingOperation() {
        return Boolean(this.pendingOperation);
    }
    /** Check if client has pending operation, used for testing */
    get hasQueuedOperation() {
        return Boolean(this.queuedOperation);
    }
    /** returns the current state of the document */
    get document() {
        return this._document;
    }
    /** sets the content of the document and emits a documentChange event */
    set document(newContent) {
        this._document = newContent;
        this.documentChangeEmitter.fire({ newContent });
    }
    /** Enqueue operation to send to the server */
    enqueueOperation(op) {
        if (this.queuedOperation) {
            this.queuedOperation = this.queuedOperation.compose(op);
        }
        else {
            this.queuedOperation = op;
        }
        if (!this.pendingOperation) {
            this.sendQueuedOperation();
        }
    }
    transformServerOperation(serverOperation) {
        // Operation comes from another client
        //
        //                         /\
        //     pendingOperation   /  \   serverOperation
        //                       /    \
        //                      /\    /
        //   queuedOperation   /  \* /   transformed[0] (new pending operation)
        //                    /    \/
        //                    \    /
        //    transformed[1]   \  /   transformed[0] (new queued operation)
        //                      \/
        // the transformed operation can be applied to the client's current document
        let incomingOperation = serverOperation;
        // Transform the pending operation to be in sync with the incoming operation
        // this results in a synced version of the pending operation and a transformation that should get applied to
        // the document or as a basis to transform the queued operation if there is one
        if (this.pendingOperation) {
            const transformed = TextOperation_1.TextOperation.transform(this.pendingOperation, incomingOperation);
            this.pendingOperation = transformed[0];
            incomingOperation = transformed[1];
        }
        // If there is a queued operation we transform it to be in sync with the incoming operation
        // this results in a synced version of the queued operation and a transformation that should get applied to the document
        if (this.queuedOperation) {
            const transformed = TextOperation_1.TextOperation.transform(this.queuedOperation, incomingOperation);
            this.queuedOperation = transformed[0];
            incomingOperation = transformed[1];
        }
        return incomingOperation;
    }
    sendPendingOperation() {
        if (this.pendingOperation) {
            const operation = this.pendingOperation;
            this.sendOperation(this.revision, operation);
        }
    }
    sendQueuedOperation() {
        if (!this.pendingOperation && this.queuedOperation) {
            this.pendingOperation = this.queuedOperation;
            this.queuedOperation = null;
            this.sendPendingOperation();
        }
    }
    /**
     * Resync with the server using the new content and last known revision
     *
     * Transforms the queued and pending operation on top of the diff between
     * our last known state and the expected state
     **/
    syncServerDocument(serverDocument, newRevision) {
        // Revert pending operation if it is not acknowledged
        if (this.pendingOperation) {
            if (this.queuedOperation) {
                this.queuedOperation = this.pendingOperation.compose(this.queuedOperation);
            }
            else {
                this.queuedOperation = this.pendingOperation;
            }
            this.pendingOperation = null;
        }
        const diff = (0, utils_1.createDiffTextOperation)(this._serverDocument, serverDocument);
        if (diff.isNoop()) {
            this.revision = newRevision;
            this.sendQueuedOperation();
            return;
        }
        // Set revision to the newRevision - 1 as `applyServerOperation` will do revision++
        // This is to ensure we are actually keeping track of the correct revision number
        this.revision = newRevision - 1;
        // We handle the diff like it is one big operation we missed
        return this.applyServerOperation(diff, types_1.OperationReason.RESYNC);
    }
    /**
     * If the OTClient errors on applying new operations from
     * the client, we can recover the state of the client document
     * by calling this method. It can potentially override server changes, but it is
     * more important that users do not loose any work
     */
    applyClientDocument(clientDocument) {
        const diff = (0, utils_1.createDiffTextOperation)(this.document, clientDocument);
        this.applyClientOperation(diff);
    }
    /**
     * Apply an operation originating from this client
     *
     * @param clientOperation the operation to apply to the document
     */
    applyClientOperation(clientOperation) {
        if (clientOperation.isNoop())
            return;
        this.document = clientOperation.apply(this.document);
        this.enqueueOperation(clientOperation);
        this.outgoingOperationEmitter.fire({
            operation: clientOperation,
            revision: this.revision,
        });
    }
    /**
     * Apply operation coming from the ot server
     *
     * @param serverOperation the operation from the server that should be applied to the document
     * @param reason the reason for the operation to occur
     */
    applyServerOperation(op, reason) {
        this.revision++;
        const transformedOp = this.transformServerOperation(op);
        this.document = transformedOp.apply(this.document);
        this._serverDocument = op.apply(this._serverDocument);
        this.sendQueuedOperation();
        this.sendOperationAck(this.revision);
        this.incomingOperationEmitter.fire({
            operation: transformedOp,
            revision: this.revision,
            reason,
        });
    }
    /**
     * On successful passing of an operation to the server. Any queued
     * operations will now be sent. The "onOperationAck" event is emitted when
     * there are no more pending operations or after we send any queued operations
     */
    serverAck() {
        if (this.pendingOperation) {
            this._serverDocument = this.pendingOperation.apply(this._serverDocument);
            this.revision++;
            this.pendingOperation = null;
            this.sendOperationAck(this.revision);
            // Pending operation has been acknowledged, if there's a queued operation we send it
            this.sendQueuedOperation();
        }
        this.operationAckEmitter.fire({
            revision: this.revision,
        });
    }
    dispose() {
        this.sendOperation = () => undefined;
        this.sendOperationAck = () => undefined;
        this.incomingOperationEmitter.dispose();
        this.operationAckEmitter.dispose();
        this.outgoingOperationEmitter.dispose();
        this.documentChangeEmitter.dispose();
    }
}
exports.Client = Client;
//# sourceMappingURL=data:application/json;base64,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